import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Headset, Eye, CheckCircle, Clock, ChatCircleText, EnvelopeSimple } from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { da } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const styles = { pending: "bg-amber-50 text-amber-700 border-amber-200", in_progress: "bg-blue-50 text-blue-700 border-blue-200", resolved: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  const labels = { pending: "Afventer", in_progress: "Under behandling", resolved: "Løst" };
  const icons = { pending: Clock, in_progress: ChatCircleText, resolved: CheckCircle };
  const Icon = icons[status] || Clock;
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${styles[status] || styles.pending}`}><Icon className="w-3 h-3 mr-1" weight="bold" />{labels[status] || status}</span>;
};

export const HandoffsPage = ({ user, setUser }) => {
  const [handoffs, setHandoffs] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSite, setFilterSite] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedHandoff, setSelectedHandoff] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => { fetchHandoffs(); fetchSites(); }, [filterSite, filterStatus]);

  const fetchHandoffs = async () => {
    try {
      const params = new URLSearchParams();
      if (filterSite !== 'all') params.append('site_key', filterSite);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      const response = await fetch(`${API}/admin/handoffs?${params}`, { credentials: 'include' });
      if (response.ok) setHandoffs(await response.json());
    } catch (error) { toast.error("Kunne ikke hente handoffs"); }
    finally { setLoading(false); }
  };

  const fetchSites = async () => {
    try { const response = await fetch(`${API}/sites`, { credentials: 'include' }); if (response.ok) setSites(await response.json()); }
    catch (error) { console.error('Failed to fetch sites:', error); }
  };

  const handleUpdateStatus = async (handoffId, newStatus) => {
    try {
      const response = await fetch(`${API}/admin/handoffs/${handoffId}?status=${newStatus}`, { method: 'PUT', credentials: 'include' });
      if (response.ok) { toast.success("Status opdateret"); fetchHandoffs(); if (selectedHandoff?.handoff_id === handoffId) setSelectedHandoff({...selectedHandoff, status: newStatus}); }
      else { const error = await response.json(); toast.error(error.detail || "Kunne ikke opdatere"); }
    } catch (error) { toast.error("Der opstod en fejl"); }
  };

  const formatDate = (dateStr) => { if (!dateStr) return "-"; try { return format(new Date(dateStr), "d. MMM yyyy HH:mm", { locale: da }); } catch { return "-"; } };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-6 md:p-8" data-testid="handoffs-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Handoffs</h1>
            <p className="text-slate-600">Henvendelser eskaleret til kundeservice</p>
          </div>
          <div className="flex gap-3">
            <Select value={filterSite} onValueChange={setFilterSite}>
              <SelectTrigger className="w-[160px]" data-testid="filter-site"><SelectValue placeholder="Alle sites" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Alle sites</SelectItem>{sites.map(site => <SelectItem key={site.site_key} value={site.site_key}>{site.brand_name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[160px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Alle status</SelectItem><SelectItem value="pending">Afventer</SelectItem><SelectItem value="in_progress">Under behandling</SelectItem><SelectItem value="resolved">Løst</SelectItem></SelectContent>
            </Select>
          </div>
        </div>

        {loading ? <div className="space-y-4">{[1,2,3].map(i => <Card key={i} className="card-default animate-pulse"><CardContent className="p-6"><div className="h-6 bg-slate-200 rounded w-3/4 mb-4" /></CardContent></Card>)}</div>
        : handoffs.length === 0 ? (
          <Card className="card-default"><CardContent className="p-12 text-center"><Headset className="w-12 h-12 text-slate-300 mx-auto mb-4" weight="duotone" /><h3 className="text-lg font-semibold text-slate-900 mb-2">Ingen handoffs</h3><p className="text-slate-600">Når kunder beder om kundeservice, vises henvendelserne her.</p></CardContent></Card>
        ) : (
          <div className="space-y-4">{handoffs.map((handoff, index) => (
            <motion.div key={handoff.handoff_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
              <Card className="card-default hover:shadow-md transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2"><StatusBadge status={handoff.status} /><Badge variant="outline" className="text-xs">{handoff.site_key}</Badge></div>
                      {handoff.customer_name && <h3 className="font-semibold text-slate-900 mb-1">{handoff.customer_name}</h3>}
                      {handoff.customer_email && <div className="flex items-center gap-1 text-sm text-slate-600 mb-2"><EnvelopeSimple className="w-4 h-4" />{handoff.customer_email}</div>}
                      {handoff.issue_summary && <p className="text-sm text-slate-600 line-clamp-2 mb-2">{handoff.issue_summary}</p>}
                      <div className="flex items-center gap-4 text-xs text-slate-500"><span>{handoff.chat_transcript?.length || 0} beskeder</span><span>Oprettet: {formatDate(handoff.created_at)}</span></div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button variant="outline" size="sm" onClick={() => { setSelectedHandoff(handoff); setDialogOpen(true); }} data-testid={`view-handoff-${handoff.handoff_id}`}><Eye className="w-4 h-4 mr-1" />Se samtale</Button>
                      {handoff.status === 'pending' && <Button variant="outline" size="sm" className="text-blue-600 hover:bg-blue-50" onClick={() => handleUpdateStatus(handoff.handoff_id, 'in_progress')}>Start</Button>}
                      {handoff.status === 'in_progress' && <Button variant="outline" size="sm" className="text-emerald-600 hover:bg-emerald-50" onClick={() => handleUpdateStatus(handoff.handoff_id, 'resolved')}><CheckCircle className="w-4 h-4 mr-1" />Løst</Button>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}</div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh]">
            <DialogHeader><DialogTitle>Samtalehistorik</DialogTitle><DialogDescription>{selectedHandoff?.customer_name || 'Anonym'} - {selectedHandoff?.site_key}</DialogDescription></DialogHeader>
            {selectedHandoff && <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  {selectedHandoff.customer_email && <p className="text-sm text-slate-600"><EnvelopeSimple className="w-4 h-4 inline mr-1" />{selectedHandoff.customer_email}</p>}
                  {selectedHandoff.page_url && <p className="text-xs text-slate-500 mt-1">Side: {selectedHandoff.page_url}</p>}
                </div>
                <StatusBadge status={selectedHandoff.status} />
              </div>
              {selectedHandoff.issue_summary && <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg"><p className="text-sm font-medium text-amber-800 mb-1">Problemresumé</p><p className="text-sm text-amber-700">{selectedHandoff.issue_summary}</p></div>}
              <ScrollArea className="h-[300px] border rounded-lg p-4">
                {selectedHandoff.chat_transcript?.length > 0 ? (
                  <div className="space-y-3">{selectedHandoff.chat_transcript.map((msg, index) => (
                    <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-lg p-3 ${msg.role === 'user' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700'}`}>
                        <p className="text-sm">{msg.content}</p>
                        <p className={`text-xs mt-1 ${msg.role === 'user' ? 'text-indigo-200' : 'text-slate-400'}`}>{formatDate(msg.timestamp)}</p>
                      </div>
                    </div>
                  ))}</div>
                ) : <p className="text-center text-slate-500 py-8">Ingen beskeder</p>}
              </ScrollArea>
              <div className="flex justify-between pt-4 border-t">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Luk</Button>
                <div className="flex gap-2">
                  {selectedHandoff.status === 'pending' && <Button variant="outline" onClick={() => handleUpdateStatus(selectedHandoff.handoff_id, 'in_progress')}>Start behandling</Button>}
                  {selectedHandoff.status === 'in_progress' && <Button className="btn-primary" onClick={() => handleUpdateStatus(selectedHandoff.handoff_id, 'resolved')}><CheckCircle className="w-4 h-4 mr-2" />Marker som løst</Button>}
                </div>
              </div>
            </div>}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
